
// src/app/modules/Operations/pages/Operations.jsx
// ─── Sety Operations Module v5 ────────────────────────────────────────────────

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Moment from 'moment';
import {
  Users, UserCheck, Clock, MapPin, AlertTriangle, Shield,
  BarChart3, FileText, Download, RefreshCw, ChevronRight,
  CheckCircle, AlertCircle, Coffee, Timer, Activity,
  Calendar, Sunrise, Sunset, Briefcase, LogOut, Eye,
  Shuffle, Search, X, Check, Flame, Car, Building2,
  DoorOpen, UserPlus, ChevronLeft, Laptop, Star,
  Wifi, Award, Globe, Layers, Navigation, TrendingUp,
  Phone, Mail, User, Hash, ClipboardList, Filter,
  StickyNote, Loader,
} from 'lucide-react';
import * as actions from '../../../Dashbord/_redux/dashboard/Actions';
import { useOrgConfig } from '../../../../../hooks/useOrgConfig';
import './operations.scss';
import { StaffCheckInModal } from '../../../AccessControl/pages/components/StaffCheckInModal';
import { ConfirmActionModal } from '../../../../components/ConfirmActionModal';

// ─── Constants ────────────────────────────────────────────────────────────────

const PROXIMITY_KM = 0.5;
const ITEMS_PER_PAGE = 25;
const STATUS_PRIORITY = { on_duty: 0, on_break: 1, late: 2, off_duty: 3 };

const STATUS_CFG = {
  on_duty:  { label: 'Working',  color: '#16a34a', bg: '#F0FDF4', dot: '#16a34a' },
  on_break: { label: 'On Break', color: '#D97706', bg: '#FFFBEB', dot: '#D97706' },
  late:     { label: 'Late',     color: '#DC2626', bg: '#FEF2F2', dot: '#DC2626' },
  off_duty: { label: 'Off Duty', color: '#9CA3AF', bg: '#F9FAFB', dot: '#DDDDE8' },
};

const VISITOR_CFG = {
  checked_in:  { label: 'Checked In', color: '#16a34a', bg: '#F0FDF4', dot: '#16a34a' },
  expected:    { label: 'Expected',   color: '#6366F1', bg: '#EEF2FF', dot: '#6366F1' },
  checked_out: { label: 'Left',       color: '#9CA3AF', bg: '#F9FAFB', dot: '#DDDDE8' },
  overstay:    { label: 'Overstay',   color: '#DC2626', bg: '#FEF2F2', dot: '#DC2626' },
  // legacy alias
  active:      { label: 'Checked In', color: '#16a34a', bg: '#F0FDF4', dot: '#16a34a' },
};

const ROLE_CFG = {
  admin:    { label: 'Admin',    color: '#DC2626', bg: '#FEF2F2' },
  manager:  { label: 'Manager',  color: '#8B5CF6', bg: '#F5F3FF' },
  security: { label: 'Security', color: '#2563EB', bg: '#EFF6FF' },
  staff:    { label: 'Staff',    color: '#5341C4', bg: '#F0EEFF' },
};

// Report types mapped to the real backend endpoint
const REPORT_TYPES = {
  business: [
    { id: 'staff_attendance',    label: 'Attendance Summary',  desc: 'Full shift log per staff per day',           color: '#2563EB', bg: '#EFF6FF', I: CheckCircle },
    { id: 'staff_performance',   label: 'Staff Performance',   desc: 'On-time rate, scores, overtime breakdown',   color: '#16a34a', bg: '#F0FDF4', I: TrendingUp },
    { id: 'staff_lateness',      label: 'Lateness Report',     desc: 'Late arrivals, avg delay, worst offenders',  color: '#D97706', bg: '#FFFBEB', I: AlertCircle },
    { id: 'visitor_analysis',    label: 'Visitor Analysis',    desc: 'Volume, frequency, duration breakdown',      color: '#8B5CF6', bg: '#F5F3FF', I: Users },
    { id: 'access_control_audit',label: 'Security Audit Log',  desc: 'Gate access, incidents, full visitor log',   color: '#5341C4', bg: '#F0EEFF', I: Shield },
  ],
  estate: [
    { id: 'visitor_analysis',    label: 'Guest Traffic',       desc: 'Volume, frequency, overstay breakdown',      color: '#D97706', bg: '#FFFBEB', I: Users },
    { id: 'access_control_audit',label: 'Security Audit Log',  desc: 'Gate access, visitor log, incidents',        color: '#5341C4', bg: '#F0EEFF', I: Shield },
    { id: 'safety_analysis',     label: 'Safety Analysis',     desc: 'Danger zones and incident data in range',    color: '#DC2626', bg: '#FEF2F2', I: AlertTriangle },
  ],
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

const PALETTE = ['#5341C4','#16a34a','#D97706','#DC2626','#6366F1','#8B5CF6','#2563EB','#0891B2'];
const avatarBg  = (id = '') => PALETTE[(id.charCodeAt(id.length - 1) || 0) % PALETTE.length];
const initials  = (name = '') => name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
const scoreClr  = (s) => s >= 85 ? '#16a34a' : s >= 70 ? '#D97706' : s >= 55 ? '#F97316' : '#DC2626';

const isRemote = (staff) => {
  if (staff.supervisor_clock_in) return false;
  if (staff.distance_from_workplace != null) return staff.distance_from_workplace > PROXIMITY_KM;
  return staff.work_mode === 'remote' || staff.is_remote === true;
};

// Normalise visitor status — backend uses 'checked_in', UI previously used 'active'
const normaliseVisitorStatus = (v) => {
  if (!v) return v;
  return { ...v, status: v.status === 'active' ? 'checked_in' : v.status };
};

// ─── Micro-components ─────────────────────────────────────────────────────────

const Av = ({ name, id, size = 34, status, statusCfg }) => {
  const bg  = avatarBg(id || name || '');
  const cfg = statusCfg || STATUS_CFG[status];
  return (
    <div style={{ position: 'relative', flexShrink: 0 }}>
      <div style={{ width: size, height: size, borderRadius: size * 0.28, background: bg, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: size * 0.36, fontWeight: 700 }}>
        {initials(name)}
      </div>
      {cfg?.dot && <div style={{ position: 'absolute', bottom: -1, right: -1, width: size * 0.3, height: size * 0.3, borderRadius: '50%', background: cfg.dot, border: '2px solid #fff' }} />}
    </div>
  );
};

const ScoreBar = ({ value }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 70 }}>
    <div style={{ flex: 1, height: 3, background: '#F1F1F5', borderRadius: 2, overflow: 'hidden' }}>
      <div style={{ width: `${value}%`, height: '100%', background: scoreClr(value), borderRadius: 2 }} />
    </div>
    <span style={{ fontSize: 11, fontWeight: 700, fontFamily: 'monospace', color: scoreClr(value), minWidth: 22 }}>{value}</span>
  </div>
);

const Pill = ({ label, color, bg }) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', padding: '3px 8px', borderRadius: 20, fontSize: 11, fontWeight: 700, color, background: bg, whiteSpace: 'nowrap' }}>
    {label}
  </span>
);

// ─── Stat Cards ───────────────────────────────────────────────────────────────

const StatCards = ({ counts, activeFilter, onFilter }) => {
  const items = [
    { key: 'all',      label: 'Total Staff',  value: counts.total,    color: '#5341C4', bg: '#F0EEFF', icon: Users,      sub: 'All members' },
    { key: 'on_duty',  label: 'Working',      value: counts.on_duty,  color: '#16a34a', bg: '#F0FDF4', icon: UserCheck,  sub: 'On shift now' },
    { key: 'on_break', label: 'On Break',     value: counts.on_break, color: '#D97706', bg: '#FFFBEB', icon: Coffee,     sub: 'Resting' },
    { key: 'late',     label: 'Late',         value: counts.late,     color: '#DC2626', bg: '#FEF2F2', icon: AlertCircle,sub: 'Behind schedule' },
    { key: 'off_duty', label: 'Off Duty',     value: counts.off_duty, color: '#9CA3AF', bg: '#F9FAFB', icon: Clock,      sub: 'Not checked in' },
  ];

  return (
    <div className="ops-stat-cards">
      {items.map(({ key, label, value, color, bg, icon: Icon, sub }) => {
        const active = activeFilter === key;
        return (
          <button
            key={key}
            className={`ops-stat-card ${active ? 'ops-stat-card--active' : ''}`}
            style={{ '--c': color, '--bg': bg }}
            onClick={() => onFilter(key)}
          >
            <div className="ops-stat-card__icon" style={{ background: bg, color }}>
              <Icon size={16} />
            </div>
            <div className="ops-stat-card__body">
              <span className="ops-stat-card__label">{label}</span>
              <div className="ops-stat-card__value" style={{ color }}>{value}</div>
              <div className="ops-stat-card__sub">{sub}</div>
            </div>
          </button>
        );
      })}
    </div>
  );
};

// ─── Staff Row ────────────────────────────────────────────────────────────────

const StaffRow = ({ staff, selected, onSelect, checked, onCheck }) => {
  const roleCfg   = ROLE_CFG[staff.role] || ROLE_CFG.staff;
  const remote    = isRemote(staff);
  const statusCfg = STATUS_CFG[staff.status] || STATUS_CFG.off_duty;

  return (
    <div
      className={`ops-staff-row ${selected ? 'ops-staff-row--sel' : ''} ${staff.status === 'off_duty' ? 'ops-staff-row--dim' : ''}`}
      onClick={() => onSelect(staff)}
    >
      <div className={`ops-cb ${checked ? 'ops-cb--on' : ''}`}
        onClick={e => { e.stopPropagation(); onCheck(staff.user_id || staff.id); }}>
        {checked && <Check size={8} color="#fff" />}
      </div>

      <Av name={staff.name} id={staff.user_id || staff.id} size={36} status={staff.status} />

      <div className="ops-staff-info">
        <div className="ops-staff-name">
          {staff.name}
          {staff.staff_role && <span className="ops-pos-tag">{staff.staff_role}</span>}
          {staff.is_supervisor && <span className="ops-chip chip-gold"><Star size={8} /> Supervisor</span>}
          {staff.fatigue_flag  && <span className="ops-chip chip-red"><Flame size={8} /> Fatigue</span>}
        </div>
        <div className="ops-staff-meta">
          <span style={{ fontSize: 11, fontWeight: 600, color: roleCfg.color, background: roleCfg.bg, padding: '1px 6px', borderRadius: 4 }}>
            {roleCfg.label}
          </span>
          {staff.department && staff.department !== staff.staff_role && (
            <span className="ops-meta-item"><Layers size={10} />{staff.department}</span>
          )}
          {staff.location_name && (
            <span className="ops-meta-item"><Building2 size={10} />{staff.location_name}</span>
          )}
          {staff.checkin_location && staff.status !== 'off_duty' && (
            <span className="ops-meta-item ops-meta-dim"><MapPin size={10} />{staff.checkin_location}</span>
          )}
        </div>
      </div>

      <div className="ops-staff-right">
        {staff.status !== 'off_duty' && (
          <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 4, display: 'flex', alignItems: 'center', gap: 3, background: remote ? '#EFF6FF' : '#F0FDF4', color: remote ? '#2563EB' : '#16a34a' }}>
            {remote ? <Laptop size={9} /> : <Building2 size={9} />}
            {remote ? 'Remote' : 'Onsite'}
          </span>
        )}
        {staff.entry_time && (
          <span style={{ fontSize: 11, fontWeight: 700, color: '#1C1C2E', fontFamily: 'monospace' }}>
            {Moment(staff.entry_time).fromNow(true)}
          </span>
        )}
        <ScoreBar value={staff.attendance_score || 0} />
        <Pill label={statusCfg.label} color={statusCfg.color} bg={statusCfg.bg} />
        <ChevronRight size={13} color={selected ? '#5341C4' : '#DDDDE8'} />
      </div>
    </div>
  );
};

// ─── Bulk Bar ─────────────────────────────────────────────────────────────────

const BulkBar = ({ count, onClear, onAction, loading }) => count ? (
  <div className="ops-bulk-bar">
    <span className="ops-bulk-tag"><Check size={11} />{count} selected</span>
    <div className="ops-bulk-actions">
      {[['break_start','Start Break',Coffee],['break_end','End Break',Activity],['notify','Notify',AlertCircle]].map(([a,l,Ic]) => (
        <button key={a} className="ops-bulk-btn" onClick={() => onAction(a)} disabled={loading}>
          <Ic size={11} />{l}
        </button>
      ))}
      <button className="ops-bulk-btn ops-bulk-btn--danger" onClick={() => onAction('clockout')} disabled={loading}>
        <LogOut size={11} /> Clock Out All
      </button>
      <button className="ops-bulk-icon" onClick={onClear}><X size={13} /></button>
    </div>
  </div>
) : null;

// ─── Staff Drawer ─────────────────────────────────────────────────────────────

const StaffDrawer = ({ staff, onClose, onClockOut, onClockIn, onChangeRole, onToggleSupervisor, navigate, organizationId }) => {
  const [tab, setTab] = useState('timeline');
  const [recentShifts,    setRecentShifts]    = useState([]);
  const [shiftsLoading,   setShiftsLoading]   = useState(false);
  const [shiftsFetched,   setShiftsFetched]   = useState(false);
  const dispatch = useDispatch();
  const roleCfg = ROLE_CFG[staff.role] || ROLE_CFG.staff;
  const remote  = isRemote(staff);

  useEffect(() => {
    if (tab === 'shifts' && !shiftsFetched && organizationId && staff.user_id) {
      setShiftsLoading(true);
      const from = Moment().subtract(30, 'days').toISOString();
      const to   = Moment().toISOString();
      dispatch(actions.fetchAllData('shift_history', {
        staff_id: staff.user_id, organization_id: organizationId,
        from_date: from, to_date: to, limit: 15, page: 1,
      })).then(res => {
        setRecentShifts(res?.data?.shifts || []);
        setShiftsFetched(true);
      }).catch(() => {}).finally(() => setShiftsLoading(false));
    }
  }, [tab, shiftsFetched, organizationId, staff.user_id, dispatch]);

  const events = [];
  if (staff.entry_time) events.push({ color: '#16a34a', label: 'Clocked In', time: staff.entry_time });
  (staff.break_logs || []).forEach(b => {
    if (b.break_start) events.push({ color: '#D97706', label: 'Break Start', time: b.break_start });
    if (b.break_end)   events.push({ color: '#16a34a', label: 'Break End',   time: b.break_end });
  });
  if (staff.status !== 'off_duty')
    events.push({ color: STATUS_CFG[staff.status]?.color || '#6366F1', label: remote ? 'Working Remotely' : 'Currently Working', live: true });

  return (
    <div className="ops-drawer">
      <div className="ops-drawer-head">
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 14 }}>
          <Av name={staff.name} id={staff.user_id || staff.id} size={44} status={staff.status} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 15, fontWeight: 800, color: '#1C1C2E' }}>{staff.name}</span>
              {staff.staff_role && <span className="ops-pos-tag" style={{ fontSize: '11px', padding: '2px 8px' }}>{staff.staff_role}</span>}
              {staff.is_supervisor && <span className="ops-chip chip-gold"><Star size={8} /> Supervisor</span>}
              {staff.fatigue_flag  && <span className="ops-chip chip-red"><Flame size={8} /> Fatigue</span>}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
              <Pill label={roleCfg.label} color={roleCfg.color} bg={roleCfg.bg} />
              {staff.employee_id && <span style={{ fontSize: 11, color: '#B5B5C3', fontWeight: 600 }}>#{staff.employee_id}</span>}
              {staff.department && staff.department !== staff.staff_role && (
                <span style={{ fontSize: 11, color: '#7E8299', display: 'flex', alignItems: 'center', gap: 3 }}>
                  <Layers size={10} />{staff.department}
                </span>
              )}
              {staff.location_name && (
                <span style={{ fontSize: 11, color: '#7E8299', display: 'flex', alignItems: 'center', gap: 3 }}>
                  <Building2 size={10} />{staff.location_name}
                </span>
              )}
            </div>
          </div>
          <button className="ops-icon-btn" onClick={onClose}><X size={14} /></button>
        </div>

        {staff.status !== 'off_duty' && (
          <div style={{ marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 6, display: 'inline-flex', alignItems: 'center', gap: 4, background: remote ? '#EFF6FF' : '#F0FDF4', color: remote ? '#2563EB' : '#16a34a' }}>
              {remote ? <Laptop size={11} /> : <Building2 size={11} />}
              {remote ? 'Working Remotely' : 'Working Onsite'}
            </span>
            {staff.distance_from_workplace != null && (
              <span style={{ fontSize: 11, color: '#7E8299' }}>
                Clocked in {(staff.distance_from_workplace * 1000).toFixed(0)}m from {staff.location_name || 'workplace'}
              </span>
            )}
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 8, marginBottom: 14 }}>
          {[
            { label: 'Score',     value: `${staff.attendance_score || 0}`, c: scoreClr(staff.attendance_score || 0) },
            { label: 'On-Time',   value: `${staff.on_time_rate || 0}%`,    c: scoreClr(staff.on_time_rate || 0) },
            { label: 'This Week', value: `${staff.shifts_this_week || 0}`, c: '#5341C4' },
          ].map((k, i) => (
            <div key={i} style={{ background: '#F8F8FC', borderRadius: 8, padding: '9px 10px', textAlign: 'center' }}>
              <div style={{ fontSize: 16, fontWeight: 800, fontFamily: 'monospace', color: k.c }}>{k.value}</div>
              <div style={{ fontSize: 10, color: '#9CA3AF', fontWeight: 600, marginTop: 2, textTransform: 'uppercase', letterSpacing: '0.3px' }}>{k.label}</div>
            </div>
          ))}
        </div>

        <div className="ops-drawer-tabs">
          {[['timeline','Timeline'],['details','Details'],['shifts','Shifts']].map(([k,l]) => (
            <button key={k} className={`ops-drawer-tab ${tab===k?'ops-drawer-tab--on':''}`} onClick={() => setTab(k)}>{l}</button>
          ))}
        </div>
      </div>

      <div className="ops-drawer-body">
        {tab === 'timeline' && (
          <>
            {events.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '28px 0', color: '#9CA3AF' }}>
                <Clock size={26} style={{ opacity: 0.25, marginBottom: 8 }} />
                <div style={{ fontSize: 13 }}>Not clocked in today</div>
                {staff.scheduled_in && <div style={{ fontSize: 11, marginTop: 4 }}>Scheduled {staff.scheduled_in}</div>}
              </div>
            ) : events.map((ev, i) => {
              const isLast = i === events.length - 1;
              return (
                <div key={i} style={{ display: 'flex', gap: 10, paddingBottom: isLast ? 0 : 16 }}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
                    <div style={{ width: 22, height: 22, borderRadius: '50%', border: `2px solid ${ev.color}`, background: ev.color + '18', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <div style={{ width: 5, height: 5, borderRadius: '50%', background: ev.color }} />
                    </div>
                    {!isLast && <div style={{ width: 2, flex: 1, background: '#EAEAF0', margin: '2px 0' }} />}
                  </div>
                  <div style={{ paddingTop: 2 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#1C1C2E' }}>{ev.label}</div>
                    <div style={{ fontSize: 11, color: '#7E8299', fontFamily: 'monospace', marginTop: 1 }}>
                      {ev.live
                        ? <span style={{ fontSize: 10, fontWeight: 700, color: ev.color, background: ev.color + '18', padding: '1px 5px', borderRadius: 4 }}>LIVE</span>
                        : Moment(ev.time).format('h:mm A')}
                    </div>
                  </div>
                </div>
              );
            })}
            {staff.scheduled_in && (
              <div style={{ marginTop: 14, padding: '10px 12px', background: '#F8F8FC', borderRadius: 8, border: '1px solid #EAEAF0' }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>Shift Schedule</div>
                <div style={{ display: 'flex', gap: 16, fontSize: 12, color: '#1C1C2E' }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><Sunrise size={10} color="#D97706" />{staff.scheduled_in}</span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><Sunset size={10} color="#6366F1" />{staff.scheduled_out || '—'}</span>
                </div>
              </div>
            )}
          </>
        )}

        {tab === 'details' && (
          <>
            {staff.fatigue_flag && (
              <div style={{ display: 'flex', gap: 10, padding: '10px 12px', background: '#FFF7ED', border: '1px solid #FDE68A', borderRadius: 8, marginBottom: 12 }}>
                <Flame size={13} color="#F97316" style={{ flexShrink: 0, marginTop: 1 }} />
                <div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#92400E' }}>Fatigue Warning</div>
                  <div style={{ fontSize: 11, color: '#B45309', marginTop: 1 }}>3+ consecutive long shifts — consider rescheduling.</div>
                </div>
              </div>
            )}
            <div style={{ padding: '12px 14px', background: '#F8F8FC', border: '1px solid #EAEAF0', borderRadius: 8, marginBottom: 10 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 10 }}>Assignment</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                {[
                  { Icon: Building2, label: 'Location', value: staff.location_name || '—' },
                  { Icon: Layers,    label: 'Department', value: staff.department || staff.space_name || '—' },
                  { Icon: MapPin,    label: 'Checked In From', value: staff.checkin_location || '—' },
                  { Icon: Laptop,    label: 'Work Mode', value: isRemote(staff) ? 'Remote' : 'Onsite' },
                ].map(({ Icon, label, value }) => (
                  <div key={label}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 3, display: 'flex', alignItems: 'center', gap: 3 }}>
                      <Icon size={10} />{label}
                    </div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#1C1C2E' }}>{value}</div>
                  </div>
                ))}
              </div>
            </div>
            <div style={{ padding: '12px 14px', background: '#F8F8FC', border: '1px solid #EAEAF0', borderRadius: 8 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 10 }}>Performance</div>
              {[{ label: 'Attendance Score', value: staff.attendance_score || 0 }].map(item => (
                <div key={item.label} style={{ marginBottom: 10 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#7E8299', marginBottom: 3 }}>
                    <span>{item.label}</span>
                    <span style={{ fontWeight: 700, color: scoreClr(item.value) }}>{item.value}%</span>
                  </div>
                  <ScoreBar value={item.value} />
                </div>
              ))}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 4 }}>
                {[
                  { label: 'Breaks Today', value: staff.break_logs?.length || 0 },
                  { label: 'Late',         value: staff.minutes_late > 0 ? `${staff.minutes_late}m` : '—' },
                  { label: 'Overtime',     value: (staff.overtime_hours || 0) > 0 ? `${staff.overtime_hours.toFixed(1)}h` : '—' },
                  { label: 'Hours Worked', value: (staff.worked_hours || 0) > 0 ? `${staff.worked_hours.toFixed(1)}h` : '—' },
                ].map(d => (
                  <div key={d.label}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.4px' }}>{d.label}</div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#1C1C2E', marginTop: 2, fontFamily: 'monospace' }}>{d.value}</div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {tab === 'shifts' && (
          <>
            {shiftsLoading ? (
              <div style={{ textAlign: 'center', padding: '28px 0', color: '#9CA3AF' }}>
                <Loader size={20} style={{ opacity: 0.4 }} className="ops-spin" />
                <div style={{ fontSize: 12, marginTop: 8 }}>Loading shifts…</div>
              </div>
            ) : recentShifts.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '28px 0', color: '#9CA3AF' }}>
                <Clock size={26} style={{ opacity: 0.25, marginBottom: 8 }} />
                <div style={{ fontSize: 13 }}>No shifts in the last 30 days</div>
              </div>
            ) : recentShifts.map((sh, i) => {
              // local_times has pre-formatted strings ("07:48 AM") — use entry_time for date
              const entryTimeStr = sh.local_times?.entry || (sh.entry_time ? Moment(sh.entry_time).format('h:mm A') : '—');
              const exitTimeStr  = sh.local_times?.exit  || (sh.exit_time  ? Moment(sh.exit_time).format('h:mm A')  : null);
              const tz     = sh.timezone || 'UTC';
              const isLive = !sh.exit_time;
              return (
                <div key={sh.log_id || i} style={{ paddingBottom: 12, marginBottom: 12, borderBottom: '1px solid #F1F1F6' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#1C1C2E' }}>
                      {sh.entry_time ? Moment(sh.entry_time).format('ddd, D MMM') : '—'}
                    </div>
                    <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                      {sh.shift_status && (
                        <span style={{
                          fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 4,
                          background: sh.shift_status === 'late' ? '#FFFBEB' : sh.shift_status === 'on_time' ? '#F0FDF4' : '#EEF2FF',
                          color:      sh.shift_status === 'late' ? '#D97706' : sh.shift_status === 'on_time' ? '#16a34a'  : '#6366F1',
                        }}>
                          {sh.shift_status === 'on_time' ? 'On Time' : sh.shift_status === 'late' ? `Late${sh.minutes_late > 0 ? ` ${sh.minutes_late}m` : ''}` : sh.shift_status?.replace('_', ' ')}
                        </span>
                      )}
                      {isLive && <span style={{ fontSize: 10, fontWeight: 700, background: '#F0FDF4', color: '#16a34a', padding: '1px 6px', borderRadius: 4 }}>● Live</span>}
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#3D4A5C', marginBottom: 3, fontFamily: 'monospace', flexWrap: 'wrap' }}>
                    <Clock size={10} />
                    {entryTimeStr} — {isLive ? 'ongoing' : (exitTimeStr || '—')}
                    <span style={{ fontSize: 10, fontWeight: 600, color: '#6366F1', background: '#EEF2FF', padding: '1px 5px', borderRadius: 3 }}>{tz}</span>
                    {sh.worked_hours > 0 && <span style={{ color: '#6366F1', fontWeight: 700 }}>({sh.worked_hours.toFixed(1)}h)</span>}
                    {sh.overtime_hours > 0 && <span style={{ color: '#D97706', fontWeight: 700 }}>+{sh.overtime_hours.toFixed(1)}h OT</span>}
                  </div>
                  {(sh.metadata?.checkin_location_name || sh.metadata?.checkout_location_name) && (
                    <div style={{ fontSize: 11, color: '#7E8299', display: 'flex', alignItems: 'center', gap: 4, marginBottom: 3 }}>
                      <MapPin size={10} />
                      {sh.metadata.checkin_location_name || sh.metadata.checkout_location_name}
                    </div>
                  )}
                  {sh.notes && (
                    <div style={{ fontSize: 11, color: '#5341C4', background: '#F0EEFF', borderRadius: 4, padding: '4px 8px', marginTop: 4, display: 'flex', gap: 5, alignItems: 'flex-start' }}>
                      <StickyNote size={10} style={{ flexShrink: 0, marginTop: 1 }} />{sh.notes}
                    </div>
                  )}
                </div>
              );
            })}
          </>
        )}
      </div>

      <div className="ops-drawer-footer">
        {staff.status === 'off_duty'
          ? <button className="ops-btn ops-btn--primary" style={{ width: '100%' }} onClick={() => onClockIn(staff)}><DoorOpen size={13} /> Clock In</button>
          : <button className="ops-btn ops-btn--danger"  style={{ width: '100%' }} onClick={() => onClockOut(staff)}><LogOut size={13} /> Clock Out</button>
        }
        <button className="ops-btn ops-btn--ghost" onClick={() => navigate(`/members/view/${staff.membership_id}`)}><Eye size={12} /> Profile</button>
      </div>
    </div>
  );
};

// ─── Visitor Row (staff-row style) ────────────────────────────────────────────

const VisitorRow = ({ visitor, selected, onSelect }) => {
  const cfg = VISITOR_CFG[visitor.status] || VISITOR_CFG.expected;
  const isOS = visitor.status === 'overstay';

  return (
    <div
      className={`ops-staff-row ${selected ? 'ops-staff-row--sel' : ''} ${visitor.status === 'checked_out' ? 'ops-staff-row--dim' : ''}`}
      style={{ borderLeftColor: selected ? '#5341C4' : isOS ? '#DC2626' : 'transparent' }}
      onClick={() => onSelect(visitor)}
    >
      <Av name={visitor.name || '?'} id={visitor.id || visitor.log_id || visitor.code_id} size={36} statusCfg={cfg} />

      <div className="ops-staff-info">
        <div className="ops-staff-name">
          {visitor.name || 'Unknown'}
          {visitor.visitor_type && visitor.visitor_type !== 'visitor' && (
            <span className="ops-pos-tag">{visitor.visitor_type.replace(/_/g, ' ')}</span>
          )}
          {isOS && <span className="ops-chip chip-red"><AlertTriangle size={8} /> Overstay</span>}
        </div>
        <div className="ops-staff-meta">
          {visitor.host && (
            <span className="ops-meta-item"><User size={10} />Host: {visitor.host}</span>
          )}
          {visitor.purpose && (
            <span className="ops-meta-item"><Briefcase size={10} />{visitor.purpose}</span>
          )}
          {visitor.phone && (
            <span className="ops-meta-item ops-meta-dim"><Phone size={10} />{visitor.phone}</span>
          )}
          {visitor.space_name && (
            <span className="ops-meta-item"><Building2 size={10} />{visitor.space_name}</span>
          )}
        </div>
      </div>

      <div className="ops-staff-right">
        {visitor.check_in ? (
          <span style={{ fontSize: 11, fontWeight: 700, color: '#1C1C2E', fontFamily: 'monospace' }}>
            {Moment(visitor.check_in).fromNow(true)}
          </span>
        ) : visitor.valid_from ? (
          <span style={{ fontSize: 11, color: '#6366F1', fontWeight: 600 }}>
            Due {Moment(visitor.valid_from).format('h:mm A')}
          </span>
        ) : null}
        <Pill label={cfg.label} color={cfg.color} bg={cfg.bg} />
        <ChevronRight size={13} color={selected ? '#5341C4' : '#DDDDE8'} />
      </div>
    </div>
  );
};

// ─── Visitor Drawer ───────────────────────────────────────────────────────────

const VisitorDrawer = ({ visitor, onClose, onCheckIn, onCheckOut, onFlag, loading, mode }) => {
  const cfg = VISITOR_CFG[visitor.status] || VISITOR_CFG.expected;
  const word = mode === 'estate' ? 'Guest' : 'Visitor';

  const fields = [
    { label: 'Host / Resident', value: visitor.host || visitor.host_name || '—', Icon: User },
    { label: 'Purpose',         value: visitor.purpose || '—',                    Icon: Briefcase },
    { label: 'Phone',           value: visitor.phone || visitor.visitor_phone || '—', Icon: Phone },
    { label: 'Email',           value: visitor.email || visitor.visitor_email || '—', Icon: Mail },
    { label: 'Space / Unit',    value: visitor.space_name || visitor.unit_number || '—', Icon: Building2 },
    { label: 'Access Code',     value: visitor.badge || visitor.code || '—',      Icon: Hash },
    { label: 'Vehicle',         value: visitor.vehicle || visitor.vehicle_plate || '—', Icon: Car },
    { label: 'Entry Point',     value: visitor.entry_point || '—',                Icon: MapPin },
    { label: 'Visits (today)',  value: visitor.frequency > 1 ? `${visitor.frequency}×` : null, Icon: ClipboardList },
  ].filter(f => f.value && f.value !== '—' && f.value !== null);

  return (
    <div className="ops-drawer">
      <div className="ops-drawer-head">
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 14 }}>
          <Av name={visitor.name || '?'} id={visitor.id || visitor.log_id} size={44} statusCfg={cfg} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 15, fontWeight: 800, color: '#1C1C2E' }}>{visitor.name || 'Unknown'}</span>
              {visitor.status === 'overstay' && (
                <span className="ops-chip chip-red"><AlertTriangle size={8} /> Overstay</span>
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
              <Pill label={cfg.label} color={cfg.color} bg={cfg.bg} />
            </div>
          </div>
          <button className="ops-icon-btn" onClick={onClose}><X size={14} /></button>
        </div>

        {/* Timeline strip */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 8, marginBottom: 14 }}>
          {[
            { label: 'Checked In',   value: visitor.check_in  ? Moment(visitor.check_in).format('h:mm A')  : '—', c: '#16a34a' },
            { label: 'Expected Out', value: visitor.expected_out ? Moment(visitor.expected_out).format('h:mm A') : visitor.check_out ? Moment(visitor.check_out).format('h:mm A') : '—', c: '#6366F1' },
          ].map((k, i) => (
            <div key={i} style={{ background: '#F8F8FC', borderRadius: 8, padding: '9px 10px', textAlign: 'center' }}>
              <div style={{ fontSize: 15, fontWeight: 800, fontFamily: 'monospace', color: k.c }}>{k.value}</div>
              <div style={{ fontSize: 10, color: '#9CA3AF', fontWeight: 600, marginTop: 2, textTransform: 'uppercase', letterSpacing: '0.3px' }}>{k.label}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="ops-drawer-body">
        <div style={{ padding: '12px 14px', background: '#F8F8FC', border: '1px solid #EAEAF0', borderRadius: 8 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 10 }}>{word} Details</div>
          {fields.map(({ label, value, Icon }) => (
            <div key={label} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '7px 0', borderBottom: '1px solid #F4F4F8' }}>
              <Icon size={12} color="#9CA3AF" style={{ marginTop: 2, flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.4px' }}>{label}</div>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#1C1C2E', marginTop: 1, wordBreak: 'break-all' }}>{value}</div>
              </div>
            </div>
          ))}
          {fields.length === 0 && (
            <div style={{ fontSize: 12, color: '#9CA3AF', textAlign: 'center', padding: '8px 0' }}>No additional details</div>
          )}
        </div>
      </div>

      <div className="ops-drawer-footer">
        {visitor.status === 'expected' && (
          <button className="ops-btn ops-btn--primary" style={{ width: '100%' }} onClick={() => onCheckIn(visitor)} disabled={loading}>
            <CheckCircle size={13} /> Confirm Arrival
          </button>
        )}
        {(visitor.status === 'checked_in' || visitor.status === 'active' || visitor.status === 'overstay') && (
          <button className="ops-btn ops-btn--danger" style={{ width: '100%' }} onClick={() => onCheckOut(visitor)} disabled={loading}>
            <LogOut size={13} /> Check Out {word}
          </button>
        )}
        {/* <button className="ops-btn ops-btn--ghost" style={{ width: '100%' }} onClick={() => onFlag(visitor)}>
          <AlertTriangle size={12} /> Flag {word}
        </button> */}
      </div>
    </div>
  );
};

// ─── Visitors Tab ─────────────────────────────────────────────────────────────

const VisitorsTab = ({ visitors, spaces, orgLocs, org, mode, setVisitorSearchLoading, onRefresh, dispatch, loading }) => {
  const [statusFilt, setStatusFilt] = useState('all');
  const [spaceFilt,  setSpaceFilt]  = useState('all');
  const [locFilt,    setLocFilt]    = useState('all');
  const [search,     setSearch]     = useState('');
  const [drawer,     setDrawer]     = useState(null);
  const [ciTarget,   setCiTarget]   = useState(null);
  const [coTarget,   setCoTarget]   = useState(null);
  const [actLoad,    setActLoad]    = useState(false);
  const [page,       setPage]       = useState(1);

  const word = mode === 'estate' ? 'Guest' : 'Visitor';

  // Normalise status aliases and apply filters client-side
  const normalised = useMemo(() => visitors.map(normaliseVisitorStatus), [visitors]);

  const filtered = useMemo(() => normalised.filter(v => {
    if (statusFilt !== 'all' && v.status !== statusFilt) return false;
    if (spaceFilt  !== 'all' && v.space_id !== spaceFilt) return false;
    if (locFilt    !== 'all' && v.location_id !== locFilt) return false;
    if (search && !v.name?.toLowerCase().includes(search.toLowerCase()) &&
        !v.host?.toLowerCase().includes(search.toLowerCase()) &&
        !v.phone?.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  }), [normalised, statusFilt, spaceFilt, locFilt, search]);

  const paged = useMemo(() => {
    const start = (page - 1) * ITEMS_PER_PAGE;
    return filtered.slice(start, start + ITEMS_PER_PAGE);
  }, [filtered, page]);

  // Counts per status for filter chips
  const counts = useMemo(() => ({
    checked_in:  normalised.filter(v => v.status === 'checked_in').length,
    expected:    normalised.filter(v => v.status === 'expected').length,
    overstay:    normalised.filter(v => v.status === 'overstay').length,
    checked_out: normalised.filter(v => v.status === 'checked_out').length,
  }), [normalised]);

  // ── Backend fetch whenever any server-side filter changes ──────────────────
  // search and status are client-side only (fast); space and location are server-side.
  // We debounce search to avoid a call on every keystroke.
  // Debounce timer ref for visitor search
  const visitorSearchTimer = React.useRef(null);

  const fetchVisitors = useCallback(async (overrides = {}) => {
    if (!org?.organization_id) return;
    const params = {};
    const sp   = overrides.spaceFilt ?? spaceFilt;
    const lc   = overrides.locFilt   ?? locFilt;
    const srch = overrides.search    ?? search;
    if (sp   !== 'all')       params.space_id    = sp;
    if (lc   !== 'all')       params.location_id = lc;
    if (srch && srch.trim())  params.name        = srch.trim();
    setVisitorSearchLoading(true);
    try {
      await dispatch(actions.fetchAllData('visitor_live', org.organization_id, params));
    } finally {
      setVisitorSearchLoading(false);
      setPage(1);
    }
  }, [org?.organization_id, spaceFilt, locFilt, search, dispatch]);

  // Re-fetch on space / location change (immediate)
  useEffect(() => {
    fetchVisitors();
  }, [spaceFilt, locFilt, org?.organization_id]);

  // Re-fetch on search change (debounced 350ms)
  useEffect(() => {
    if (!org?.organization_id) return;
    clearTimeout(visitorSearchTimer.current);
    visitorSearchTimer.current = setTimeout(() => {
      fetchVisitors();
    }, 350);
    return () => clearTimeout(visitorSearchTimer.current);
  }, [search, org?.organization_id]);

  const handleCheckIn = async (v) => {
    setActLoad(true);
    try {
      if (v.log_id) {
        // Visitor already has a log (e.g. was pre-checked-in as expected via a different flow)
        await dispatch(actions.addData('supervisor_visitor_checkin', { organization_id: org.organization_id }, v.log_id));
      } else if (v.code_id) {
        // Expected visitor — no log yet, create one from the access code
        await dispatch(actions.addData('visitor_checkin_from_code', {
          organization_id: org.organization_id,
          code_id:         v.code_id,
          entry_point:     'Operations Panel',
        }));
      } else {
        return; // nothing to work with
      }
      onRefresh();
      setDrawer(null);
    } finally { setActLoad(false); }
  };

  const handleCheckOut = async (v) => {
    if (!v.log_id) return; // can't check out someone who never checked in
    setActLoad(true);
    try {
      await dispatch(actions.addData('supervisor_visitor_checkout', { organization_id: org.organization_id, reason: 'Checked out via Operations panel' }, v.log_id));
      onRefresh();
      setDrawer(null);
    } finally { setActLoad(false); }
  };

  const handleFlag = async (v) => {
    // Best we can do without a dedicated flag endpoint from the ops side
    // This logs a note — extend as needed
    console.log('Flag visitor:', v);
  };

  const STATUS_FILTERS = [
    { key: 'all',         label: 'All' },
    { key: 'checked_in',  label: 'Checked In',  badge: counts.checked_in  },
    { key: 'expected',    label: 'Expected',     badge: counts.expected    },
    { key: 'overstay',    label: 'Overstay',     badge: counts.overstay, danger: true },
    { key: 'checked_out', label: 'Left'                                   },
  ];

  return (
    <div className="ops-tab-body">
      {/* Search + filters */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        <div className="ops-search-box" style={{ flex: 1, minWidth: 200 }}>
          {loading && search
            ? <RefreshCw size={13} color="#5341C4" style={{ animation: 'ops-spin 0.7s linear infinite', flexShrink: 0 }} />
            : <Search size={13} color="#9CA3AF" style={{ flexShrink: 0 }} />
          }
          <input value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} placeholder={`Search ${word.toLowerCase()}s…`} />
          {search && <button style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex' }} onClick={() => setSearch('')}><X size={12} color="#9CA3AF" /></button>}
        </div>

        {/* Space filter */}
        {spaces?.length > 0 && (
          <select className="ops-select" value={spaceFilt} onChange={e => {
              setSpaceFilt(e.target.value);
              fetchVisitors({ spaceFilt: e.target.value });
            }}>
            <option value="all">All Areas</option>
            {spaces.map(s => <option key={s.space_id || s.name} value={s.space_id || s.name}>{s.name}</option>)}
          </select>
        )}

        {/* Location filter */}
        {orgLocs?.length > 1 && (
          <select className="ops-select" value={locFilt} onChange={e => {
              setLocFilt(e.target.value);
              fetchVisitors({ locFilt: e.target.value });
            }}>
            <option value="all">All Locations</option>
            {orgLocs.map(l => <option key={l.location_id} value={l.location_id}>{l.name}</option>)}
          </select>
        )}

        {(search || spaceFilt !== 'all' || locFilt !== 'all') && (
          <button className="ops-btn ops-btn--ghost" style={{ padding: '6px 12px', fontSize: 11 }}
            onClick={() => { setSearch(''); setSpaceFilt('all'); setLocFilt('all'); setPage(1); }}>
            <X size={11} /> Clear
          </button>
        )}
      </div>

      {/* Status chips */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {STATUS_FILTERS.map(f => (
          <button key={f.key} onClick={() => { setStatusFilt(f.key); setPage(1); }}
            style={{ padding: '5px 12px', borderRadius: 20, border: '1px solid', fontSize: 11, fontWeight: 700, cursor: 'pointer', transition: 'all 0.12s',
              background: statusFilt === f.key ? '#5341C4' : '#fff',
              borderColor: statusFilt === f.key ? '#5341C4' : '#EAEAF0',
              color: statusFilt === f.key ? '#fff' : '#7E8299',
              display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            {f.label}
            {f.badge > 0 && (
              <span style={{ background: f.danger ? '#DC2626' : (statusFilt === f.key ? 'rgba(255,255,255,0.3)' : '#5341C4'), color: '#fff', fontSize: 9, fontWeight: 700, padding: '0 5px', borderRadius: 8, lineHeight: 1.6 }}>
                {f.badge}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Main list + drawer grid */}
      <div className={drawer ? 'ops-staff-layout ops-staff-layout--split' : 'ops-staff-layout'}>
        <div className="sd-card">
          <div className="sd-card__header">
            <div className="sd-card__title">
              <Users size={13} />
              {filtered.length} {word}{filtered.length !== 1 ? 's' : ''}
              {filtered.length !== normalised.length && (
                <span style={{ fontSize: 11, color: '#9CA3AF', fontWeight: 400 }}>of {normalised.length}</span>
              )}
            </div>
            <span className="sd-card__meta">
              <span className="ops-live-dot" style={{ marginRight: 5 }} />
              Live · {Moment().format('h:mm A')}
            </span>
          </div>

          {loading && visitors.length === 0 ? (
            <>{[1,2,3,4].map(i => (
              <div key={i} style={{ height: 62, background: 'linear-gradient(90deg,#F8F8FC 25%,#EDEDF3 50%,#F8F8FC 75%)', backgroundSize:'200% 100%', animation:'ops-shimmer 1.3s infinite', borderBottom:'1px solid #F4F4F8' }} />
            ))}</>
          ) : filtered.length === 0 ? (
            <div className="sd-empty">
              <Users size={30} />
              <p>No {word.toLowerCase()}s yet</p>
              {(search || statusFilt !== 'all') && <small>Try adjusting your filters</small>}
            </div>
          ) : (
            <>
              {paged.map(v => (
                <VisitorRow
                  key={v.log_id || v.id || v.code_id}
                  visitor={v}
                  selected={drawer?.log_id === v.log_id && drawer?.id === v.id}
                  onSelect={vv => setDrawer(prev => (prev?.log_id === vv.log_id && prev?.id === vv.id) ? null : vv)}
                />
              ))}
              <Pages total={filtered.length} page={page} onPage={setPage} />
            </>
          )}
        </div>

        {drawer && (
          <VisitorDrawer
            visitor={drawer}
            onClose={() => setDrawer(null)}
            onCheckIn={handleCheckIn}
            onCheckOut={handleCheckOut}
            onFlag={handleFlag}
            loading={actLoad}
            mode={mode}
          />
        )}
      </div>
    </div>
  );
};

// ─── Analytics Tab ────────────────────────────────────────────────────────────

const AnalyticsTab = ({ staff, visitors, mode }) => {
  const onDuty   = staff.filter(s => s.status === 'on_duty').length;
  const onBreak  = staff.filter(s => s.status === 'on_break').length;
  const remoteN  = staff.filter(s => isRemote(s) && s.status !== 'off_duty').length;
  const fatigue  = staff.filter(s => s.fatigue_flag).length;
  const avgScore = staff.length ? Math.round(staff.reduce((a, s) => a + (s.attendance_score || 0), 0) / staff.length) : 0;

  const leaderboard = [...staff].filter(s => (s.attendance_score || 0) > 0 && (s.shifts_this_week || 0) > 0)
    .sort((a, b) => (b.attendance_score || 0) - (a.attendance_score || 0)).slice(0, 5);

  const normVisitors = useMemo(() => visitors.map(normaliseVisitorStatus), [visitors]);

  if (mode === 'estate') {
    const active   = normVisitors.filter(v => v.status === 'checked_in').length;
    const overstay = normVisitors.filter(v => v.status === 'overstay').length;
    const expected = normVisitors.filter(v => v.status === 'expected').length;
    const left     = normVisitors.filter(v => v.status === 'checked_out').length;
    return (
      <div className="ops-tab-body">
        <div className="sd-kpi-grid">
          {[
            { icon: <UserCheck size={18} />,    label: 'Active Guests',   value: active,   bg: '#F0FDF4', color: '#16a34a', sub: 'on premises now' },
            { icon: <AlertTriangle size={18}/>,  label: 'Overstays',       value: overstay, bg: '#FEF2F2', color: '#DC2626', sub: 'past expected time' },
            { icon: <Calendar size={18} />,      label: 'Expected Today',  value: expected, bg: '#EEF2FF', color: '#6366F1', sub: 'pre-registered' },
            { icon: <CheckCircle size={18}/>,    label: 'Departed Today',  value: left,     bg: '#F9FAFB', color: '#9CA3AF', sub: 'visits complete' },
          ].map((k,i) => (
            <div key={i} className="sd-kpi">
              <div className="sd-kpi__icon" style={{ background: k.bg, color: k.color }}>{k.icon}</div>
              <div className="sd-kpi__body">
                <span className="sd-kpi__label">{k.label}</span>
                <div className="sd-kpi__value">{k.value}</div>
                <div className="sd-kpi__footer">{k.sub}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="ops-tab-body">
      <div className="sd-kpi-grid" style={{ marginBottom: 16 }}>
        {[
          { icon: <Shield size={18} />,      label: 'Avg Score',    value: avgScore, unit: '/100', bg: '#F0EEFF', color: '#5341C4', sub: 'all staff' },
          { icon: <CheckCircle size={18} />, label: 'On Duty',      value: onDuty,               bg: '#F0FDF4', color: '#16a34a', sub: `${onBreak} on break` },
          { icon: <Laptop size={18} />,      label: 'Remote Today', value: remoteN,              bg: '#EFF6FF', color: '#2563EB', sub: `${onDuty - remoteN} onsite` },
          { icon: <Flame size={18} />,       label: 'Fatigue Flags',value: fatigue,              bg: '#FEF2F2', color: '#DC2626', sub: fatigue > 0 ? 'review schedule' : 'all clear' },
        ].map((k, i) => (
          <div key={i} className="sd-kpi">
            <div className="sd-kpi__icon" style={{ background: k.bg, color: k.color }}>{k.icon}</div>
            <div className="sd-kpi__body">
              <span className="sd-kpi__label">{k.label}</span>
              <div className="sd-kpi__value">{k.value}{k.unit && <span className="unit">{k.unit}</span>}</div>
              <div className="sd-kpi__footer">{k.sub}</div>
            </div>
          </div>
        ))}
      </div>
      <div className="sd-grid">
        <div className="sd-card">
          <div className="sd-card__header"><div className="sd-card__title"><Award size={14} /> Leaderboard</div><span className="sd-card__meta">This week</span></div>
          <div className="sd-card__body" style={{ padding: leaderboard.length ? 0 : 20 }}>
            {leaderboard.length === 0
              ? <div style={{ textAlign: 'center', color: '#9CA3AF' }}><Award size={26} style={{ opacity: 0.25, marginBottom: 8 }} /><div style={{ fontSize: 13 }}>Scores appear after shifts</div></div>
              : leaderboard.map((s, i) => (
                <div key={s.user_id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 16px', borderBottom: i < leaderboard.length - 1 ? '1px solid #F4F4F8' : 'none' }}>
                  <span style={{ width: 22, fontSize: 13, textAlign: 'center' }}>{['🥇','🥈','🥉'][i] || i + 1}</span>
                  <Av name={s.name} id={s.user_id} size={28} />
                  <div style={{ flex: 1 }}><div style={{ fontSize: 12, fontWeight: 700, color: '#1C1C2E', marginBottom: 3 }}>{s.name}</div><ScoreBar value={s.attendance_score} /></div>
                  {i === 0 && <span style={{ fontSize: 10, fontWeight: 700, background: '#FFFBEB', color: '#D97706', padding: '2px 7px', borderRadius: 4 }}>Top</span>}
                </div>
              ))
            }
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div className="sd-card">
            <div className="sd-card__header"><div className="sd-card__title"><Wifi size={14} /> Work Mode</div></div>
            <div className="sd-card__body">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                {[
                  { l: 'Onsite', n: onDuty - remoteN, c: '#16a34a', bg: '#F0FDF4', I: Building2 },
                  { l: 'Remote', n: remoteN,           c: '#2563EB', bg: '#EFF6FF', I: Laptop   },
                ].map(({ l, n, c, bg, I }) => (
                  <div key={l} style={{ padding: '12px 14px', background: bg, borderRadius: 8 }}>
                    <I size={15} color={c} style={{ marginBottom: 6 }} />
                    <div style={{ fontSize: 22, fontWeight: 800, color: c, fontFamily: 'monospace' }}>{n}</div>
                    <div style={{ fontSize: 11, color: c, fontWeight: 700 }}>{l}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Reports Tab — real /reports endpoint ─────────────────────────────────────

const ReportsTab = ({ mode, spaces, orgLocs, organizationId, dispatch, user }) => {
  const [sel,      setSel]      = useState(null);
  const [from,     setFrom]     = useState(Moment().startOf('month').format('YYYY-MM-DD'));
  const [to,       setTo]       = useState(Moment().format('YYYY-MM-DD'));
  const [spaceF,   setSpaceF]   = useState('');
  const [locF,     setLocF]     = useState('');
  const [email,    setEmail]    = useState('');
  const [gen,      setGen]      = useState(false);
  const [done,     setDone]     = useState(null); // { report_id, status }
  const [err,      setErr]      = useState(null);

  const reports = REPORT_TYPES[mode] || REPORT_TYPES.business;

  const go = async () => {
    if (!sel || gen) return;
    setGen(true); setDone(null); setErr(null);
    try {
      const payload = {
        organization_id: organizationId,
        type: sel,
        filters: {
          from_date: from || undefined,
          to_date:   to   || undefined,
          space_id:  spaceF || undefined,
          location_id: locF || undefined,
        },
        email_to: email || undefined,
      };
      // Call the real reports endpoint
      const res = await dispatch(actions.addData('request_report', payload));
      setDone(res);
    } catch (e) {
      setErr(e?.response?.data?.message || 'Report generation failed');
    } finally { setGen(false); }
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 20, alignItems: 'start' }}>
      <div>
        <div style={{ fontSize: 12, fontWeight: 700, color: '#1C1C2E', marginBottom: 12 }}>Select Report Type</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {reports.map(r => {
            const on = sel === r.id;
            return (
              <button key={r.id} onClick={() => { setSel(r.id); setDone(null); setErr(null); }}
                style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '13px 16px', borderRadius: 10,
                  border: `${on ? 2 : 1}px solid ${on ? r.color : '#EAEAF0'}`, background: '#fff', cursor: 'pointer',
                  textAlign: 'left', transition: 'all 0.12s', width: '100%' }}>
                <div style={{ width: 38, height: 38, borderRadius: 10, background: r.bg, color: r.color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <r.I size={16} />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#1C1C2E' }}>{r.label}</div>
                  <div style={{ fontSize: 11, color: '#7E8299', marginTop: 2 }}>{r.desc}</div>
                </div>
                {on && <CheckCircle size={15} color={r.color} style={{ flexShrink: 0 }} />}
              </button>
            );
          })}
        </div>
      </div>

      <div className="sd-card">
        <div className="sd-card__header"><div className="sd-card__title">Parameters</div></div>
        <div className="sd-card__body">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>Date Range</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                <input type="date" className="ops-input" value={from} onChange={e => setFrom(e.target.value)} />
                <input type="date" className="ops-input" value={to}   onChange={e => setTo(e.target.value)} />
              </div>
            </div>

            {spaces?.length > 0 && (
              <div>
                <div style={{ fontSize: 10, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>Space / Department</div>
                <select className="ops-select" style={{ width: '100%' }} value={spaceF} onChange={e => setSpaceF(e.target.value)}>
                  <option value="">All Spaces</option>
                  {spaces.map(s => <option key={s.space_id || s.name} value={s.space_id || s.name}>{s.name}</option>)}
                </select>
              </div>
            )}

            {orgLocs?.length > 1 && (
              <div>
                <div style={{ fontSize: 10, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>Location</div>
                <select className="ops-select" style={{ width: '100%' }} value={locF} onChange={e => setLocF(e.target.value)}>
                  <option value="">All Locations</option>
                  {orgLocs.map(l => <option key={l.location_id} value={l.location_id}>{l.name}</option>)}
                </select>
              </div>
            )}

            <div>
              <div style={{ fontSize: 10, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>Email Report To</div>
              <input type="email" className="ops-input" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@company.com (optional)" />
            </div>

            {done && (
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '10px 12px', background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 8 }}>
                <CheckCircle size={14} color="#16a34a" style={{ flexShrink: 0, marginTop: 1 }} />
                <div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#16a34a' }}>Report queued successfully</div>
                  <div style={{ fontSize: 11, color: '#15803d', marginTop: 2 }}>
                    {email ? `Results will be emailed to ${email}` : 'Processing in background — check your email shortly'}
                  </div>
                </div>
              </div>
            )}

            {err && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 12px', background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 8 }}>
                <AlertCircle size={13} color="#DC2626" />
                <span style={{ fontSize: 12, fontWeight: 600, color: '#DC2626' }}>{err}</span>
              </div>
            )}

            <button
              className={`ops-btn ${sel ? 'ops-btn--primary' : 'ops-btn--disabled'}`}
              onClick={go}
              disabled={!sel || gen}
              style={{ width: '100%' }}
            >
              {gen
                ? <><RefreshCw size={13} className="ops-spin" /> Generating…</>
                : <><Download size={13} /> Generate Report</>
              }
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Pagination ───────────────────────────────────────────────────────────────

const Pages = ({ total, page, onPage }) => {
  const n = Math.ceil(total / ITEMS_PER_PAGE);
  if (n <= 1) return null;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '11px 16px', borderTop: '1px solid #F4F4F8' }}>
      <button className="ops-btn ops-btn--ghost" style={{ padding: '5px 10px', fontSize: 11 }} onClick={() => onPage(page-1)} disabled={page===1}><ChevronLeft size={12} /> Prev</button>
      <div style={{ display: 'flex', gap: 3, flex: 1, justifyContent: 'center' }}>
        {Array.from({ length: Math.min(n,7) }, (_,i) => {
          let p = i+1;
          if (n>7) p = page<=4 ? i+1 : page>=n-3 ? n-6+i : page-3+i;
          return <button key={p} onClick={() => onPage(p)} style={{ width: 27, height: 27, borderRadius: 6, border: '1px solid', fontSize: 11, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', background: p===page?'#5341C4':'#fff', borderColor: p===page?'#5341C4':'#EAEAF0', color: p===page?'#fff':'#7E8299' }}>{p}</button>;
        })}
      </div>
      <button className="ops-btn ops-btn--ghost" style={{ padding: '5px 10px', fontSize: 11 }} onClick={() => onPage(page+1)} disabled={page===n}>Next <ChevronRight size={12} /></button>
      <span style={{ fontSize: 11, color: '#9CA3AF', flexShrink: 0 }}>{total} total</span>
    </div>
  );
};

// ─── Main ─────────────────────────────────────────────────────────────────────

export function Operations() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { getLabel, org } = useOrgConfig();

  const mode = ['residential_estate','estate'].includes(org?.type) ? 'estate' : 'business';

  const [tab,       setTab]       = useState(mode === 'estate' ? 'visitors' : 'live');
  const [statFilt,  setStatFilt]  = useState('all');
  const [deptFilt,  setDeptFilt]  = useState('all');
  const [locFilt,   setLocFilt]   = useState('all');
  const [q,         setQ]         = useState('');
  const [drawer,    setDrawer]    = useState(null);
  const [bulk,      setBulk]      = useState([]);
  const [bulkLoad,  setBulkLoad]  = useState(false);
  const [coTarget,  setCoTarget]  = useState(null);
  const [coLoading, setCoLoading] = useState(false);
  const [showCI,    setShowCI]    = useState(false);
  const [directCI,  setDirectCI]  = useState(null);
  const [loading,          setLoading]          = useState(false);
  const [staffSearchLoading, setStaffSearchLoading] = useState(false);
  const [visitorSearchLoading, setVisitorSearchLoading] = useState(false);
  const [page,      setPage]      = useState(1);

  const { liveStaff, visitors, residentLive, spaces } = useSelector(s => ({
    liveStaff:    s.dashboard.liveStaff    || [],
    visitors:     s.dashboard.visitorLive  || [],
    residentLive: s.dashboard.residentLive || [],
    spaces:       s.dashboard.space        || [],
  }), shallowEqual);

  useEffect(() => { if (org?.organization_id) load(); }, [org?.organization_id]);
  useEffect(() => { setPage(1); }, [q, statFilt, deptFilt, locFilt]);

  // ── Debounced staff filter → re-fetch live_overview with query params ─────────
  const staffSearchTimer = React.useRef(null);
  useEffect(() => {
    if (!org?.organization_id || mode !== 'business') return;
    // Show spinner immediately on keystroke / filter change
    if (q || deptFilt !== 'all' || locFilt !== 'all') setStaffSearchLoading(true);
    clearTimeout(staffSearchTimer.current);
    staffSearchTimer.current = setTimeout(async () => {
      const params = {};
      if (deptFilt !== 'all') params.space_id    = deptFilt;
      if (locFilt  !== 'all') params.location_id = locFilt;
      if (q && q.trim())      params.name        = q.trim();
      try {
        await dispatch(actions.fetchAllData('live_overview', org.organization_id, params));
      } finally {
        setStaffSearchLoading(false);
        setPage(1);
      }
    }, 400);
    return () => clearTimeout(staffSearchTimer.current);
  }, [deptFilt, locFilt, q, org?.organization_id, mode]);

  function load() {
    if (!org?.organization_id) return;
    setLoading(true);
    const id = org.organization_id;
    const calls = [dispatch(actions.fetchAllData('spaces', id))];
    if (mode === 'business') {
      calls.push(
        dispatch(actions.fetchAllData('live_overview', id)),
        dispatch(actions.fetchAllData('visitor_live', id)),
      );
    } else {
      calls.push(
        dispatch(actions.fetchAllData('visitor_live', id)),
        dispatch(actions.fetchAllData('resident_live', id)),
      );
    }
    Promise.all(calls).finally(() => setLoading(false));
  }

  // Staff list sorted + filtered
  const sorted = useMemo(() => [...liveStaff].sort((a,b) => {
    const pa = STATUS_PRIORITY[a.status] ?? 3, pb = STATUS_PRIORITY[b.status] ?? 3;
    return pa !== pb ? pa - pb : (b.attendance_score||0) - (a.attendance_score||0);
  }), [liveStaff]);

  const filteredStaff = useMemo(() => sorted.filter(s => {
    // Name/dept search — client-side (backend doesn't support text search on live_overview)
    if (q && !s.name?.toLowerCase().includes(q.toLowerCase()) &&
             !s.department?.toLowerCase().includes(q.toLowerCase()) &&
             !s.staff_role?.toLowerCase().includes(q.toLowerCase())) return false;
    if (statFilt !== 'all' && s.status !== statFilt) return false;
    // dept filter: deptFilt is a space_id, match against s.space_id
    if (deptFilt !== 'all' && s.space_id !== deptFilt) return false;
    if (locFilt  !== 'all' && s.location_id !== locFilt) return false;
    return true;
  }), [sorted, q, statFilt, deptFilt, locFilt]);

  const pagedStaff = useMemo(() => {
    const start = (page-1)*ITEMS_PER_PAGE;
    return filteredStaff.slice(start, start+ITEMS_PER_PAGE);
  }, [filteredStaff, page]);

  const counts = useMemo(() => ({
    total:    liveStaff.length,
    on_duty:  liveStaff.filter(s=>s.status==='on_duty').length,
    on_break: liveStaff.filter(s=>s.status==='on_break').length,
    late:     liveStaff.filter(s=>s.status==='late').length,
    off_duty: liveStaff.filter(s=>s.status==='off_duty').length,
  }), [liveStaff]);

  const depts   = spaces;
  const orgLocs = org?.locations || [];

  const toggleBulk = id => setBulk(p => p.includes(id) ? p.filter(i=>i!==id) : [...p,id]);

  const handleClockOut = useCallback(async () => {
    if (!coTarget) return;
    setCoLoading(true);
    try {
      await dispatch(actions.addData('force_clock_out', { organization_id: org.organization_id, reason: 'Supervisor clock-out from Operations panel' }, coTarget.user_id || coTarget.id));
      setCoTarget(null); setDrawer(null); load();
    } finally { setCoLoading(false); }
  }, [coTarget, org, dispatch]);

  const handleDirectCI = async (staff) => {
    try {
      await dispatch(actions.addData('manager_clock_in', { organization_id: org.organization_id, entry_point: 'Operations Panel' }, staff.user_id || staff.id));
      setDrawer(null); load();
    } catch {}
  };

  const handleChangeRole = async (staff, role) => {
    await dispatch(actions.addData('update_member_role', { organization_id: org.organization_id, new_role: role }, staff.membership_id));
    setDrawer(p => p ? {...p, role} : p);
    load();
  };

  const handleToggleSuper = async (staff) => {
    await dispatch(actions.addData('toggle_supervisor', { organization_id: org.organization_id, is_supervisor: !staff.is_supervisor }, staff.membership_id));
    setDrawer(p => p ? {...p, is_supervisor: !p.is_supervisor} : p);
    load();
  };

  const handleBulkAction = async (action) => {
    setBulkLoad(true);
    try {
      await dispatch(actions.addData('bulk_staff_action', { organization_id: org.organization_id, staff_ids: bulk, action, reason: action === 'clockout' ? 'Bulk clock-out' : undefined }));
      setBulk([]); load();
    } finally { setBulkLoad(false); }
  };

  const tabDefs = mode === 'estate'
    ? [['visitors', UserCheck, 'Guests'],    ['analytics', BarChart3, 'Analytics'], ['reports', FileText, 'Reports']]
    : [['live',     Activity,  'Live Staff'], ['visitors',  UserCheck, 'Visitors'],  ['analytics', BarChart3, 'Analytics'], ['reports', FileText, 'Reports']];

  // Staff list content
  const staffListContent = (
    <div>
      <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' }}>
        <div className="ops-search-box" style={{ flex: 1, minWidth: 200 }}>
          {staffSearchLoading
            ? <RefreshCw size={13} color="#5341C4" style={{ animation: 'ops-spin 0.7s linear infinite', flexShrink: 0 }} />
            : <Search size={13} color="#9CA3AF" style={{ flexShrink: 0 }} />
          }
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search name or department…" />
          {q && <button style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex' }} onClick={() => setQ('')}><X size={12} color="#9CA3AF" /></button>}
        </div>
        {depts.length > 0 && (
          <select className="ops-select" value={deptFilt} onChange={e => setDeptFilt(e.target.value)}>
            <option value="all">All Departments</option>
            {depts.map(d => <option key={d.space_id || d.name} value={d.space_id || d.name}>{d.name}</option>)}
          </select>
        )}
        {orgLocs.length > 1 && (
          <select className="ops-select" value={locFilt} onChange={e => setLocFilt(e.target.value)}>
            <option value="all">All Locations</option>
            {orgLocs.map(l => <option key={l.location_id} value={l.location_id}>{l.name}</option>)}
          </select>
        )}
        {(deptFilt !== 'all' || locFilt !== 'all' || statFilt !== 'all' || q) && (
          <button className="ops-btn ops-btn--ghost" style={{ padding: '6px 12px', fontSize: 11 }}
            onClick={() => { setDeptFilt('all'); setLocFilt('all'); setStatFilt('all'); setQ(''); }}>
            <X size={11} /> Clear
          </button>
        )}
      </div>

      <div className="sd-card">
        <div className="sd-card__header">
          <div className="sd-card__title">
            <Users size={13} />
            {filteredStaff.length} Member{filteredStaff.length !== 1 ? 's' : ''}
            {filteredStaff.length !== liveStaff.length && (
              <span style={{ fontSize: 11, color: '#9CA3AF', fontWeight: 400 }}>of {liveStaff.length}</span>
            )}
          </div>
          <span className="sd-card__meta">
            <span className="ops-live-dot" style={{ marginRight: 5 }} />
            Live · {Moment().format('h:mm A')}
          </span>
        </div>

        {loading && liveStaff.length === 0 ? (
          <>{[1,2,3,4,5].map(i => (
            <div key={i} style={{ height: 62, background: 'linear-gradient(90deg,#F8F8FC 25%,#EDEDF3 50%,#F8F8FC 75%)', backgroundSize:'200% 100%', animation:'ops-shimmer 1.3s infinite', borderBottom:'1px solid #F4F4F8' }} />
          ))}</>
        ) : filteredStaff.length === 0 ? (
          <div className="sd-empty">
            <Users size={30} />
            <p>No staff match your filters</p>
            {(q || statFilt !== 'all' || deptFilt !== 'all' || locFilt !== 'all') && (
              <small>Try adjusting your search or filters</small>
            )}
          </div>
        ) : (
          <>
            {pagedStaff.map(s => (
              <StaffRow
                key={s.user_id || s.id}
                staff={s}
                selected={drawer?.user_id === s.user_id}
                onSelect={st => setDrawer(prev => prev?.user_id === st.user_id ? null : st)}
                checked={bulk.includes(s.user_id || s.id)}
                onCheck={toggleBulk}
              />
            ))}
            <Pages total={filteredStaff.length} page={page} onPage={setPage} />
          </>
        )}
      </div>
    </div>
  );

  return (
    <div className="security-dashboard">
      {/* Header */}
      <div className="ops-page-header">
        <div className="ops-page-header__left">
          <div className="ops-page-header__icon"><Activity size={18} color="#5341C4" /></div>
          <div>
            <h1 className="ops-page-header__title">Operations</h1>
            <div className="ops-page-header__meta">
              <span style={{ fontSize: 12, fontWeight: 700, color: '#5341C4' }}>{org?.name}</span>
              <span className="ops-page-header__dot">·</span>
              <span style={{ fontSize: 12, color: '#7E8299' }}>{mode === 'estate' ? 'Estate Management' : 'Workforce Oversight'}</span>
              {mode === 'business' && counts.on_duty > 0 && (
                <>
                  <span className="ops-page-header__dot">·</span>
                  <span className="ops-live-dot" />
                  <span style={{ fontSize: 12, color: '#16a34a', fontWeight: 600 }}>{counts.on_duty} working now</span>
                </>
              )}
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {mode === 'business' && (
            <button className="sd-btn" style={{ background: '#5341C4', color: '#fff', border: 'none', fontWeight: 700 }} onClick={() => setShowCI(true)}>
              <UserPlus size={13} /> Self Clock In Staff
            </button>
          )}
          <button className="sd-btn" onClick={load} disabled={loading}>
            <RefreshCw size={13} className={loading ? 'spinning' : ''} /> {loading ? 'Loading…' : 'Refresh'}
          </button>
        </div>
      </div>

      {/* Stat cards (business only) */}
      {mode === 'business' && (
        <StatCards counts={counts} activeFilter={statFilt} onFilter={(key) => { setStatFilt(key); setTab('live'); }} />
      )} 

      {/* Tab bar */}
      <div className="ops-tabbar">
        {tabDefs.map(([key, Icon, label]) => (
          <button key={key} className={`ops-tab ${tab===key?'ops-tab--active':''}`} onClick={() => setTab(key)}>
            <Icon size={13} /> {label}
            {key === 'live' && counts.on_duty > 0 && (
              <span style={{ fontSize: 10, fontWeight: 700, background: '#F0FDF4', color: '#16a34a', padding: '1px 6px', borderRadius: 10, marginLeft: 2 }}>
                {counts.on_duty}
              </span>
            )}
            {key === 'visitors' && (() => {
              const norm = visitors.map(normaliseVisitorStatus);
              const active = norm.filter(v => v.status === 'checked_in' || v.status === 'overstay').length;
              return active > 0 ? (
                <span style={{ fontSize: 10, fontWeight: 700, background: '#EEF2FF', color: '#6366F1', padding: '1px 6px', borderRadius: 10, marginLeft: 2 }}>
                  {active}
                </span>
              ) : null;
            })()}
          </button>
        ))}
      </div>

      <BulkBar count={bulk.length} onClear={() => setBulk([])} onAction={handleBulkAction} loading={bulkLoad} />

      {/* Live Staff Tab */}
      {tab === 'live' && (
        <div className={drawer ? 'ops-staff-layout ops-staff-layout--split' : 'ops-staff-layout'}>
          {staffListContent}
          {drawer && (
            <StaffDrawer
              key={drawer.user_id || drawer.id}
              staff={drawer}
              onClose={() => setDrawer(null)}
              onClockOut={s => setCoTarget(s)}
              onClockIn={handleDirectCI}
              onChangeRole={handleChangeRole}
              onToggleSupervisor={handleToggleSuper}
              navigate={navigate}
              organizationId={org?.organization_id}
            />
          )}
        </div>
      )}

      {/* Visitors Tab */}
      {tab === 'visitors' && (
        <VisitorsTab
          visitors={visitors}
          spaces={spaces}
          orgLocs={orgLocs}
          org={org}
          mode={mode}
          onRefresh={load}
          dispatch={dispatch}
          loading={visitorSearchLoading}
          setVisitorSearchLoading = {setVisitorSearchLoading}
        />
      )}

      {tab === 'analytics' && <AnalyticsTab staff={liveStaff} visitors={visitors} mode={mode} />}

      {tab === 'reports' && (
        <ReportsTab
          mode={mode}
          spaces={spaces}
          orgLocs={orgLocs}
          organizationId={org?.organization_id}
          dispatch={dispatch}
        />
      )}

      <StaffCheckInModal
        show={showCI}
        onHide={() => { setShowCI(false); setDirectCI(null); }}
        organizationId={org?.organization_id}
        preselectedStaff={directCI}
        onSuccess={() => { setShowCI(false); setDirectCI(null); load(); }}
        getLabel={getLabel}
      />

      <ConfirmActionModal
        show={!!coTarget}
        onHide={() => setCoTarget(null)}
        onConfirm={handleClockOut}
        title="Clock Out Staff"
        message={
          <div>
            <p>Clock out <strong>{coTarget?.name}</strong>?</p>
            {coTarget?.entry_time && <p style={{ marginTop: 8, fontSize: 12, color: '#7E8299' }}>Started {Moment(coTarget.entry_time).fromNow()} · {Moment(coTarget.entry_time).format('h:mm A')}</p>}
          </div>
        }
        confirmText="Clock Out"
        type="warning"
        loading={coLoading}
      />
    </div>
  );
}